import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router"

import type { Route } from "./+types/root"
import { AppProviders } from "./providers"
import { Analytics } from "./shared/components/Analytics"
import { AnalyticsTracker } from "./shared/components/AnalyticsTracker"
import { Toaster } from "./shared/components/ui/sonner"
import "./shared/i18n"
import "./app.css"

const STRUCTURED_DATA_HTML = {
  __html: JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FinancialService",
    name: "MesaOTC",
    legalName: "MesaOTC Tecnologia Ltda.",
    url: "https://mesaotc.com",
    logo: "https://mesaotc.com/brand/logo-mesaotc-light.png",
    image: "https://mesaotc.com/og-image-1200x630.png",
    description:
      "Infraestrutura de cobrança e liquidação multiativo. Cobre em uma moeda, receba em outra. Fiat, stablecoin e cripto em um único fluxo.",
    serviceType: [
      "Cobrança multiativo",
      "Liquidação cross-currency",
      "API de pagamentos",
    ],
    areaServed: {
      "@type": "Country",
      name: "Brasil",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "São Paulo",
      addressRegion: "SP",
      addressCountry: "BR",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "contato@mesaotc.com",
      availableLanguage: ["Portuguese", "English"],
    },
    sameAs: [
      "https://linkedin.com/company/mesaotc",
      "https://x.com/mesaotc",
      "https://github.com/mesaotc",
    ],
  }),
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
        <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
        <meta httpEquiv="Permissions-Policy" content="camera=(), microphone=(), geolocation=()" />
        <meta name="theme-color" content="#0B1A3B" />
        <meta name="author" content="MesaOTC" />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="msapplication-TileColor" content="#0A0E1A" />
        <meta name="apple-mobile-web-app-title" content="MesaOTC" />
        <meta name="application-name" content="MesaOTC" />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/inter-400.woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/inter-500.woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/manrope-600.woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="prefetch"
          as="fetch"
          href="/geo/countries.geojson"
          type="application/json"
          crossOrigin="anonymous"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={STRUCTURED_DATA_HTML}
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){try{var s=localStorage.getItem('theme');var m=window.matchMedia('(prefers-color-scheme: dark)').matches;var d=s?s==='dark':m;var r=document.documentElement;if(d){r.classList.add('dark');r.style.colorScheme='dark';}else{r.style.colorScheme='light';}}catch(e){}})();",
          }}
        />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning>
        <AppProviders>
          {children}
          <Toaster />
          <AnalyticsTracker />
        </AppProviders>
        <Analytics />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const is404 = isRouteErrorResponse(error) && error.status === 404
  const isDev = import.meta.env.DEV
  const message = error instanceof Error ? error.message : String(error)
  const stack = error instanceof Error ? error.stack : undefined

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAFAFB] px-6 py-12">
      <div className="w-full max-w-2xl space-y-4 text-center">
        <h1 className="font-display text-4xl font-semibold text-[#0B1A3B]">
          {is404 ? "Página não encontrada" : "Algo deu errado"}
        </h1>
        <p className="text-[#6B7280]">
          {is404
            ? "O endereço acessado não existe."
            : "Ocorreu um erro inesperado. Tente novamente em instantes."}
        </p>
        {isDev && !is404 && (
          <pre className="mt-6 max-h-80 overflow-auto rounded-lg border border-red-200 bg-red-50 p-4 text-left font-mono text-xs text-red-900">
            <strong>{message}</strong>
            {stack ? `\n\n${stack}` : null}
          </pre>
        )}
      </div>
    </div>
  )
}
